import { Config } from "../config";
import { UserData } from "../Models/userData";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import en from "i18n-iso-countries/langs/en.json";

declare var braintree: any;


export class PaymentService {
    loaderElement: HTMLElement = document.querySelector('.loader') as HTMLElement;
    nameElement: HTMLElement = document.querySelector('#recap-name') as HTMLElement;
    addressElement: HTMLElement = document.querySelector('#recap-address') as HTMLElement;
    immatElement: HTMLElement = document.querySelector('#recap-immat') as HTMLElement;
    carElement: HTMLElement = document.querySelector('#recap-car') as HTMLElement;


    cgvElement: HTMLInputElement = document.querySelector('#consent-cgv') as HTMLInputElement;
    retractElement: HTMLInputElement = document.querySelector('#consent-retractation') as HTMLInputElement;

    cgverrorElement: HTMLElement = document.querySelector('#cgv-error') as HTMLElement;
    retracterrorElement: HTMLElement = document.querySelector('#retract-error') as HTMLElement;

    submitElement: HTMLButtonElement = document.querySelector("#pay") as HTMLButtonElement;
    errorElement: HTMLElement = document.querySelector("#error") as HTMLElement;
    cardNumberSelector = "#cardnumber";
    cardDateSelector = "#carddate";
    cardCVCSelector = "#cardcvc";
    cardCSS = {
        input: {
            'height': "30px",
            'font-size': '18px',
            'font-family': 'monospace'
        }
    };

    successURL = "https://ecovehicule.fr/commande-confirmee";

    braintreeTokenURL = "https://getBraintreeToken" + Config.functionUrl + "";
    braintreePaidURL = "https://getBraintreePayment" + Config.functionUrl + "";
    braintreeAmountURL =  "https://getBraintreeAmount" + Config.functionUrl + "";
    trackURL = "https://trackStep" + Config.functionUrl + "";

    braintreeInstance: any;
    braintreeThreeDSInstance: any;

    isInit = false;

    constructor() {
        this.loader(true);
        countries.registerLocale(fr);
        countries.registerLocale(en);
    }

    async init(user: UserData): Promise<void> {

        //recap uniquement affiché : un champ vide ne doit jamais bloquer le paiement
        const u = user as any;
        this.nameElement.innerHTML = u.invoiceName || "";
        this.addressElement.innerHTML = [u.invoiceAddress1, u.invoiceAddress2].filter(Boolean).join(" ")
            + ", " + [u.invoiceZip, u.invoiceCity, u.invoiceCountry].filter(Boolean).join(" ");

        this.immatElement.innerHTML = u.immat || "";
        const immatDate = this.formatDate(u.immatDate);
        this.carElement.innerHTML = (immatDate ? immatDate + " - " : "") + "Vignette Crit’Air demandée";

        this.submitElement.disabled = true;
        //init payment button
        if (!this.isInit) {
            this.submitElement.addEventListener('click', async (event) => {
                event.preventDefault();
                this.track(user.email, "clic_paiement");

                if (!this.cgvElement.checked) {
                    this.cgverrorElement.style.color = "#990208";
                    this.track(user.email, "cgv_manquantes");
                    return;
                }
                if (!this.retractElement.checked) {
                    this.retracterrorElement.style.color = "#990208";
                    this.track(user.email, "cgv_manquantes");
                    return;
                }


                this.loader(true);

                const target = event.currentTarget as HTMLButtonElement;
                target.disabled = true;

                this.errorElement.style.display = 'none';
                //submit payment
                await this.submitPayment(user);
                this.loader(false);
            })
            this.isInit = true;
            this.loader(false);
        }

        //init braintree api
        try {
            await this.initBraintree();
            this.track(user.email, "braintree_ok");
        } catch (e: any) {
            console.log(e);
            this.track(user.email, "braintree_erreur", e?.message || String(e));
            this.loaderElement.style.display = 'none';
            this.submitElement.style.display = 'none';
            this.showError("Le module de paiement n'a pas pu se charger. Veuillez recharger la page.");
        }
    }

    async submitPayment(user: any) {
        try {
            let payload = await this.braintreeInstance.tokenize(); // get braintree token
            let amount = await this.getAmount();

            //verify with 3DS if necessary
            let threedsPayload = await this.braintreeThreeDSInstance.verifyCard({
                onLookupComplete: function (data:any , next:any) {
                    next();
                },
                amount: amount,
                required: false,
                nonce: payload.nonce,
                // bin: payload.details.bin,
                email: user.email,
                challengeRequested: false,
                cardAddChallengeRequested: true,
                billingAddress: this.getBillingAddress(user)
            });

            // 4. Vérification correcte du 3DS
            if (!threedsPayload.liabilityShifted && threedsPayload.liabilityShiftPossible) {
                this.errorElement.style.display = 'block';
                this.errorElement.innerHTML = "Échec de l'authentification 3D Secure.";
                this.track(user.email, "3ds_echec", threedsPayload.threeDSecureInfo?.status || "");
                this.submitElement.disabled = false;
                return;
            }


            await this.braintreeShowNonce(threedsPayload, user);
        } catch (e: any) {
            console.log(e);
            this.track(user.email, "erreur_client", [e?.code, e?.message || String(e)].filter(Boolean).join(" | "));
            this.showError(this.getClientErrorMessage(e));
            this.submitElement.disabled = false;
        }
    }

    //adresse de facturation pour le 3DS, mêmes champs que côté serveur (facturation puis adresse postale)
    getBillingAddress(user: any) {
        const address: any = {
            givenName: user.firstname,
            surname: user.lastname,
            streetAddress: user.invoiceAddress1 || user.address1,
            extendedAddress: user.invoiceAddress2 || user.address2,
            locality: user.invoiceCity || user.city,
            postalCode: (user.invoiceZip || user.zip)?.toString(),
            countryCodeAlpha2: this.getCountryCode(user.invoiceCountry || user.country)
        };
        //pas de champ vide envoyé à la banque
        Object.keys(address).forEach(key => {
            if (!address[key]) delete address[key];
        });
        return address;
    }

    //code pays ISO à 2 lettres, undefined si inconnu (pas de "FR" par défaut)
    getCountryCode(country: any): string | undefined {
        if (!country) return undefined;
        const value = country.toString().trim();
        if (value.length == 2 && countries.isValid(value.toUpperCase())) return value.toUpperCase();
        return countries.getAlpha2Code(value, "fr") || countries.getAlpha2Code(value, "en") || undefined;
    }

    getClientErrorMessage(e: any): string {
        const code: string = e?.code || "";
        if (code == "PAYMENT_UNCONFIRMED")
            return "Nous n'avons pas pu confirmer votre paiement. Merci de nous contacter à contact@ecovehicule.fr avant de réessayer.";
        if (code == "HOSTED_FIELDS_FIELDS_EMPTY")
            return "Veuillez saisir les informations de votre carte.";
        if (code == "HOSTED_FIELDS_FIELDS_INVALID")
            return "Les informations de votre carte sont incomplètes ou invalides.";
        if (code.startsWith("THREEDS_"))
            return "L'authentification 3D Secure n'a pas pu aboutir. Veuillez réessayer.";
        return "Une erreur est survenue, veuillez réessayer. Si le problème persiste, contactez-nous à contact@ecovehicule.fr.";
    }

    showError(message: string) {
        this.errorElement.style.display = 'block';
        this.errorElement.innerHTML = message;
    }

    showUserNotFound() {
        this.loaderElement.style.display = 'none';
        this.submitElement.style.display = 'none';
        this.errorElement.style.display = 'block';
        this.errorElement.innerHTML = "Votre demande n'a pas été retrouvée, contactez-nous à contact@ecovehicule.fr";
    }

    //affiche une date en JJ/MM/AAAA quel que soit le format reçu de Zapier
    formatDate(value: any): string {
        if (!value) return "";
        const str = value.toString().trim();
        const iso = str.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})/); // AAAA-MM-JJ, AAAA-MM-JJTHH:mm...
        if (iso) return iso[3].padStart(2, "0") + "/" + iso[2].padStart(2, "0") + "/" + iso[1];
        const fr = str.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})/); // JJ/MM/AAAA, JJ-MM-AAAA, JJ.MM.AAAA
        if (fr) return fr[1].padStart(2, "0") + "/" + fr[2].padStart(2, "0") + "/" + fr[3];
        return str; // format inconnu : affiché tel quel
    }

    //tracking panier abandonné, ne doit jamais bloquer le paiement
    track(email: string, step: string, info: string = "") {
        try {
            fetch(this.trackURL + "?e=" + encodeURIComponent(email) + "&s=" + step + "&i=" + encodeURIComponent(info), { keepalive: true })
                .catch(() => { });
        } catch (e) { }
    }

    async initBraintree(): Promise<void> {
        //get unique token
        let braintreeTokenResponse = await fetch(this.braintreeTokenURL);
        let braintreeToken = (await (braintreeTokenResponse.json() as any)).client_token;

        //init braintree element
        let braintreeInstances = await Promise.all([
            braintree.hostedFields.create({
                authorization: braintreeToken,
                styles: this.cardCSS,
                fields: {
                    number: {
                        selector: this.cardNumberSelector,
                        placeholder: '4111 1111 1111 1111'
                    },
                    cvv: {
                        selector: this.cardCVCSelector,
                        placeholder: '123'
                    },
                    expirationDate: {
                        selector: this.cardDateSelector,
                        placeholder: '12 / 34'
                    }
                }
            }),
            braintree.threeDSecure.create({
                authorization: braintreeToken,
                version: 2
            })
        ]);


        this.braintreeInstance = braintreeInstances[0];
        this.braintreeThreeDSInstance = braintreeInstances[1];


        this.braintreeInstance.on('validityChange', (event:any) => {

            var field = event.fields[event.emittedBy];
            (field.container as HTMLElement).classList.remove('is-valid');
            (field.container as HTMLElement).classList.remove('is-invalid');
            this.submitElement.disabled = false;;
            if (field.isValid) {
                (field.container as HTMLElement).classList.add('is-valid');
            } else if (field.isPotentiallyValid) {
                // skip adding classes if the field is
                // not valid, but is potentially valid
            } else {
                (field.container as HTMLElement).classList.add('is-invalid');
                this.submitElement.disabled = true;
            }
        });

    }

    //validate payment & send data to BO to validate server side
    async braintreeShowNonce(payload: any, user: UserData) {

        let rs;
        try {
            let r = await fetch(this.braintreePaidURL + "?e=" + encodeURIComponent(JSON.stringify(user.email)) + "&n=" + encodeURIComponent(payload.nonce));
            rs = await r.json();
        } catch (e: any) {
            //réponse perdue ou illisible : on ne sait pas si le client a été débité
            throw { code: "PAYMENT_UNCONFIRMED", message: e?.message || String(e) };
        }
        if (rs.success) {
            document.location = this.successURL;
        }
        else {
            this.errorElement.style.display = 'block';
            this.errorElement.innerHTML = rs.message;
            await this.braintreeInstance.teardown();
            await this.braintreeThreeDSInstance.teardown();
            await this.init(user);
        }
    }

    async getAmount(): Promise<number> {
        let rawResponse = await fetch(this.braintreeAmountURL);
        let response = await rawResponse.json();
        return response.amount;
    }

    loader(show: boolean) {
        this.submitElement.style.display = !show ? 'flex' : 'none';
        this.loaderElement.style.display = show ? 'block' : 'none';
    }
}